import AndroidIcon from '@mui/icons-material/Android';
import DevicesIcon from '@mui/icons-material/Devices';
import OldIphoneIcon from '@mui/icons-material/PhoneIphone';
import TabletIcon from '@mui/icons-material/Tablet';
import IphoneIcon from 'assets/icons/iphone.svg';
import ChromeIcon from 'assets/icons/chrome.svg';
import EdgeIcon from 'assets/icons/edge.svg';
import FirefoxIcon from 'assets/icons/firefox.svg';
import SafariIcon from 'assets/icons/safari.svg';
import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { palette } from 'styles/theme';
/**
 * Wrapper component that adds Telegram-style rounded background to icons
 */
const IconWrapper = ({ children, bgColor, className = '' }) => {
  return (
    <div
      className={className}
      style={{
        backgroundColor: bgColor,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        padding: '8px',
      }}
    >
      {React.cloneElement(children, {
        style: {
          color: '#ffffff',
          width: '24px',
          height: '24px',
        },
      })}
    </div>
  );
};

IconWrapper.propTypes = {
  children: PropTypes.element.isRequired,
  bgColor: PropTypes.string.isRequired,
  className: PropTypes.string,
};

/**
 * Returns an appropriate icon component for a device based on its operating system and name
 * @param {string} os - Device operating system
 * @param {string} deviceName - Friendly device name
 * @param {string} friendlyName - Friendly device name
 * @param {string} className - CSS class to apply to the wrapper
 * @returns {React.ReactElement} Icon component with Telegram-style background
 */
const getDeviceIcon = (os, deviceName, friendlyName, className) => {
  let icon = <DevicesIcon />;
  let bgColor = '#8e8e93'; // Default gray

  // Use brand primary color for iOS devices if theme is provided
  const brandColor = palette?.primary[50] || '#0088cc';

  // Match iOS devices
  if (
    os?.toLowerCase() === 'ios' ||
    os?.includes('iPhone') ||
    friendlyName?.includes('iPhone') ||
    friendlyName?.includes('iPad')
  ) {
    bgColor = brandColor; // Use brand color instead of Telegram's blue

    if (friendlyName?.includes('iPad')) {
      icon = <TabletIcon />;
    }
    // Use OldIphoneIcon for iPhone models with physical buttons
    else if (
      deviceName?.match(/iPhone [1-8](,|$)/) ||
      deviceName?.includes('iPhone8,4') || // SE 1st gen
      deviceName?.includes('iPhone12,8') || // SE 2nd gen
      deviceName?.includes('iPhone14,6') // SE 3rd gen
    ) {
      icon = <OldIphoneIcon />;
    } else {
      icon = <IphoneIcon />;
    }
  }
  // Match Android devices
  else if (os?.toLowerCase() === 'android' || friendlyName?.includes('Android')) {
    icon = <AndroidIcon />;
    bgColor = '#4caf50'; // Android green
  }

  // Browser specific icons
  else if (friendlyName?.includes('Chrome')) {
    icon = <ChromeIcon />;
    bgColor = '#4caf50'; // Chrome green
  } else if (friendlyName?.includes('Firefox')) {
    icon = <FirefoxIcon />;
    bgColor = '#ff9800'; // Firefox orange
  } else if (friendlyName?.includes('Safari')) {
    icon = <SafariIcon />;
    bgColor = '#2196f3'; // Safari blue
  } else if (friendlyName?.includes('Edge')) {
    icon = <EdgeIcon />;
    bgColor = '#0078d7'; // Edge blue
  }

  return (
    <IconWrapper bgColor={bgColor} className={className}>
      {icon}
    </IconWrapper>
  );
};

/**
 * A wrapper for getDeviceIcon that provides themed icons
 */
export const DeviceIconWithTheme = ({ os, deviceName, friendlyName, className }) => {
  const theme = useTheme();
  return getDeviceIcon(os, deviceName, friendlyName, className, theme);
};

DeviceIconWithTheme.propTypes = {
  os: PropTypes.string,
  deviceName: PropTypes.string,
  friendlyName: PropTypes.string,
  className: PropTypes.string,
};

export default getDeviceIcon;
